import { z } from 'zod';

/**
 * Region geometry: normalized 0..1 coordinates relative to the screenshot.
 * Storing normalized values means the script keeps working if a screenshot
 * is later re-uploaded at a different resolution.
 */
export const RectSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
});
export type Rect = z.infer<typeof RectSchema>;

const baseRegion = {
  id: z.string().uuid(),
  rect: RectSchema,
  label: z.string().max(120).default(''),
};

// Which mouse action activates a click-correct/click-error region. Defaults
// to a plain left click — the only kind that existed before this field did.
// 'hover' fires with no click at all, once the pointer has rested inside the
// region for a short dwell (see HOVER_DWELL_MS in the Player) — for
// simulating cascading hover-driven menus (e.g. a right-click context menu's
// "New" item expanding a submenu just by pointing at it).
export const CLICK_TYPES = ['left', 'double', 'right', 'hover'] as const;
export const ClickTypeSchema = z.enum(CLICK_TYPES);
export type ClickType = z.infer<typeof ClickTypeSchema>;

export const ClickCorrectRegionSchema = z.object({
  ...baseRegion,
  kind: z.literal('click-correct'),
  // If null, falls back to step.successNext.
  leadsTo: z.string().uuid().nullable(),
  clickType: ClickTypeSchema.default('left'),
  // When a step has more than one valid way to succeed (e.g. click the
  // expand chevron, or double-click the item itself), the instruction demo
  // should still only act out ONE of them — the others stay fully usable in
  // training/test, just not shown as "the" way. Defaults to true so a step
  // with only one click-correct region behaves exactly as before.
  showInInstruction: z.boolean().default(true),
  // Separate from showInInstruction: which region training/test falls back
  // to when it needs to point at "the" correct one — currently only the
  // stage-3 reveal highlight, but anything added later that needs a single
  // canonical target should use this too. Without it, that always fell back
  // to array order, which could silently pick an "alternative" region the
  // author never intended to be the featured one.
  primary: z.boolean().default(true),
});

export const ClickErrorRegionSchema = z.object({
  ...baseRegion,
  kind: z.literal('click-error'),
  feedback: z.string().max(2000).default(''),
  correctivePath: z.array(z.string().uuid()).default([]),
  // First step of the corrective mini-flow to return to after completing it.
  returnToStepId: z.string().uuid().nullable().default(null),
  clickType: ClickTypeSchema.default('left'),
});

// How the learner confirms what they typed and moves on. Enter/Tab match how
// most real forms work; blur covers "clicked elsewhere (e.g. the next field)"
// without requiring a dedicated key; button is an explicit opt-in for fields
// where none of the above fits. At least one must stay selected.
export const CONFIRM_TRIGGERS = ['enter', 'tab', 'blur', 'button'] as const;
export const ConfirmTriggerSchema = z.enum(CONFIRM_TRIGGERS);
export type ConfirmTrigger = z.infer<typeof ConfirmTriggerSchema>;

export const InputRegionSchema = z.object({
  ...baseRegion,
  kind: z.literal('input'),
  // Either a literal expected value or a regex (string source). The literal
  // variant's flags are all opt-in leniency toggles for common "close
  // enough" answers (a URL with/without protocol or trailing slash) without
  // needing a hand-written regex; regex remains the escape hatch for
  // anything these don't cover.
  expected: z.union([
    z.object({
      kind: z.literal('literal'),
      value: z.string(),
      caseSensitive: z.boolean().default(false),
      ignoreProtocol: z.boolean().default(false), // ignores a leading http:// or https://
      ignoreTrailingSlash: z.boolean().default(false), // ignores one trailing '/'
    }),
    z.object({ kind: z.literal('regex'), pattern: z.string(), flags: z.string().default('i') }),
  ]),
  placeholder: z.string().max(200).default(''),
  feedbackOnWrong: z.string().max(2000).default(''),
  leadsTo: z.string().uuid().nullable(),
  // Override the default dark input text — useful when the underlying
  // screenshot has a dark-themed field where the default wouldn't read well.
  textColor: z.string().max(20).nullable().default(null),
  confirmTriggers: z.array(ConfirmTriggerSchema).min(1).default(['enter', 'tab']),
});

export const DragTargetSchema = z.object({
  id: z.string().uuid(),
  rect: RectSchema,
  result: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('correct'), leadsTo: z.string().uuid().nullable() }),
    z.object({
      kind: z.literal('specific-error'),
      feedback: z.string().max(2000).default(''),
      correctivePath: z.array(z.string().uuid()).default([]),
    }),
    z.object({ kind: z.literal('general-error'), feedback: z.string().max(2000).default('') }),
  ]),
});
export type DragTarget = z.infer<typeof DragTargetSchema>;

export const DragSourceRegionSchema = z.object({
  ...baseRegion,
  kind: z.literal('drag-source'),
  targets: z.array(DragTargetSchema).default([]),
});

// A freely placed, non-interactive caption/annotation box — not a click
// target at all, just styled text laid over the screenshot. Shown in every
// mode (instruction/training/test) unchanged, since it doesn't affect
// scoring. Groundwork for offering a chapter as a plain walkthrough/
// presentation, where this kind of on-screen annotation is the main content.
export const TextRegionSchema = z.object({
  ...baseRegion,
  kind: z.literal('text'),
  text: z.string().max(2000).default(''),
  fontSize: z.number().int().min(8).max(120).default(16),
  textColor: z.string().max(20).default('#0f172a'),
  backgroundColor: z.string().max(20).default('#ffffff'),
  borderColor: z.string().max(20).default('#0f172a'),
  shadow: z.boolean().default(false),
});

export const RegionSchema = z.discriminatedUnion('kind', [
  ClickCorrectRegionSchema,
  ClickErrorRegionSchema,
  InputRegionSchema,
  DragSourceRegionSchema,
  TextRegionSchema,
]);
export type Region = z.infer<typeof RegionSchema>;

export const StepSchema = z.object({
  id: z.string().uuid(),
  chapterId: z.string().uuid(),
  // Free-form internal label for authors; never shown to learners.
  authorLabel: z.string().max(120).default(''),
  screenshotAssetId: z.string().uuid().nullable(),
  instruction: z.string().max(4000).default(''),       // shown in instruction + as TTS source
  taskPrompt: z.string().max(4000).default(''),        // shown in training/test ("opdracht")
  stageFeedback1: z.string().max(2000).default(''),    // overrides generic stage-1 error message
  fallbackFeedback: z.string().max(2000).default(''),  // when learner clicks a non-region area
  successNext: z.string().uuid().nullable(),           // happy-flow next step (terminal if null)
  stepKind: z.enum(['main', 'corrective']).default('main'),
  // When set: the step is inert (ignores all click/keyboard/input), shows for
  // this many ms, then auto-advances to successNext (or ends the chapter if
  // successNext is null). Used for loading/transition screens and end screens.
  autoAdvanceMs: z.number().int().min(0).max(60000).nullable().default(null),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Step = z.infer<typeof StepSchema>;

export const StepWithRegionsSchema = StepSchema.extend({
  regions: z.array(RegionSchema).default([]),
});
export type StepWithRegions = z.infer<typeof StepWithRegionsSchema>;

export const AssetSchema = z.object({
  id: z.string().uuid(),
  projectVersionId: z.string().uuid(),
  filename: z.string(),
  mimeType: z.string(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  byteSize: z.number().int().positive(),
  createdAt: z.string().datetime(),
});
export type Asset = z.infer<typeof AssetSchema>;
